import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlateCombobox } from "@/components/PlateCombobox";
import { api, errMsg } from "@/lib/api";

const NONE = "__none";
const blank = { number: "", internal_number: "", status: "DISPONIBILE", unavailable_reason: "", station_id: "", vehicle_id: NONE, vehicle_ref: "", area_c: false, notes: "" };
const F = ({ label, children, span }) => <div className={`space-y-1.5 ${span ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;

export const TelepassDialog = ({ item, vehicles, stations, taken, isAdmin, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (item) setF({ ...blank, station_id: stations[0]?.id || "", ...item, vehicle_id: item.vehicle_id || NONE });
  }, [item, stations]);
  if (!item) return null;
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const station = isAdmin ? f.station_id : stations[0]?.id;
  const opts = vehicles.filter((v) => v.station_id === station && (!taken.has(v.id) || v.id === item.vehicle_id));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, vehicle_id: f.vehicle_id === NONE ? null : f.vehicle_id };
      if (item.id) await api.put(`/telepass/${item.id}`, payload); else await api.post("/telepass", payload);
      toast.success("Telepass salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="telepass-dialog">
        <DialogHeader>
          <DialogTitle>{item.id ? `Telepass ${item.number}` : "Nuovo Telepass"}</DialogTitle>
          <DialogDescription>L'associazione al furgone è facoltativa.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Numero Telepass"><Input value={f.number} onChange={set("number")} className="uppercase" data-testid="telepass-number-input" /></F>
          <F label="Numero interno"><Input value={f.internal_number} onChange={set("internal_number")} data-testid="telepass-internal-input" /></F>
          <F label="Stato">
            <Select value={f.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="telepass-status-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="DISPONIBILE">Disponibile</SelectItem><SelectItem value="NON_DISPONIBILE">Non disponibile</SelectItem></SelectContent>
            </Select>
          </F>
          {isAdmin && <F label="Stazione di riferimento">
            <Select value={f.station_id || undefined} onValueChange={(v) => setF((p) => ({ ...p, station_id: v, vehicle_id: NONE }))}>
              <SelectTrigger data-testid="telepass-station-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>}
          {f.status === "NON_DISPONIBILE" && <F label="Non disponibile per" span><Input value={f.unavailable_reason} onChange={set("unavailable_reason")} placeholder="Es. non funzionante, sostituito da..." data-testid="telepass-reason-input" /></F>}
          <F label="Furgone associato" span>
            <PlateCombobox value={f.vehicle_id} onChange={set("vehicle_id")} testId="telepass-vehicle-select"
              options={[{ value: NONE, label: "— Nessun furgone" }, ...opts.map((v) => ({ value: v.id, label: `${v.plate} · ${v.model}` }))]} />
            {f.vehicle_id === NONE && f.vehicle_ref && <p className="text-xs text-amber-700">Dal file: "{f.vehicle_ref}" (non trovato in flotta)</p>}
          </F>
          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 sm:col-span-2" data-testid="telepass-areac-label">
            <Checkbox checked={f.area_c} onCheckedChange={(c) => set("area_c")(!!c)} data-testid="telepass-areac-checkbox" />
            <span className="text-sm font-medium">Associato all'Area C</span>
          </label>
          <F label="Note" span><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="telepass-notes-input" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving || !f.number.trim()} data-testid="telepass-save-button">{saving ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
